import { useEffect, useRef } from 'react';
import { useSession } from '../context/SessionContext';
import { useNotification } from '../context/NotificationContext';

/**
 * Custom hook to listen for "Preparing" status notifications and trigger notifications
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether the subscription is active
 * @param {function} options.onPreparing - Callback function when preparing status is received
 * @param {boolean} options.showNotification - Whether to show a notification modal
 * @param {string} options.notificationTitle - Custom title for the notification
 * @param {string} options.notificationMessage - Custom message for the notification
 */
export function usePreparingNotification(options = {}) {
    const {
        enabled = true,
        onPreparing = null,
        showNotification = true,
        notificationTitle = "Charger Status Update",
        notificationMessage = "Your charger is preparing to start charging. Please wait..."
    } = options;

    const { state } = useSession();
    const { notify } = useNotification();
    const previousStatusRef = useRef(null);
    const hasNotifiedRef = useRef(false);
    
    // Store callbacks in refs to avoid triggering effect on every render
    const onPreparingRef = useRef(onPreparing);
    const notifyRef = useRef(notify);
    
    // Update refs on every render - just ref assignment, no side effects
    onPreparingRef.current = onPreparing;
    notifyRef.current = notify;

    useEffect(() => {
        if (!enabled) return;

        const currentStatus = state.live?.status;
        const previousStatus = previousStatusRef.current;

        // Debug logging for all status changes
        if (currentStatus !== previousStatus) {
            console.log(' Status change detected:', {
                from: previousStatus,
                to: currentStatus,
                stationId: state.stationId,
                timestamp: new Date().toISOString(),
                enabled,
                hasNotified: hasNotifiedRef.current
            });
        }

        // Check if status changed to "Preparing"
        if (currentStatus === "Preparing" && previousStatus !== "Preparing" && !hasNotifiedRef.current) {
            console.log(' Preparing notification triggered:', {
                currentStatus,
                previousStatus,
                stationId: state.stationId,
                timestamp: new Date().toISOString()
            });

            // Execute custom callback if provided
            if (typeof onPreparingRef.current === 'function') {
                try {
                    onPreparingRef.current({
                        status: currentStatus,
                        previousStatus,
                        stationId: state.stationId,
                        timestamp: state.live?.ts || Date.now()
                    });
                } catch (error) {
                    console.error('Error executing onPreparing callback:', error);
                }
            }

            // Show notification if enabled
            if (showNotification && notifyRef.current) {
                notifyRef.current({
                    title: notificationTitle,
                    message: notificationMessage,
                    action: {
                        label: "OK",
                        onClick: () => {
                            console.log('Preparing notification acknowledged');
                        }
                    }
                });
            }

            hasNotifiedRef.current = true;
        }

        // Reset notification flag when status changes away from Preparing
        if (currentStatus !== "Preparing") {
            hasNotifiedRef.current = false;
        }

        // Update previous status reference
        previousStatusRef.current = currentStatus;

    }, [state.live?.status, state.stationId, enabled, showNotification, notificationTitle, notificationMessage]);

    // Return current status and utility functions
    return {
        currentStatus: state.live?.status,
        isPreparing: state.live?.status === "Preparing",
        stationId: state.stationId,
        lastUpdate: state.live?.ts,
        // Utility function to manually reset the notification flag
        resetNotificationFlag: () => {
            hasNotifiedRef.current = false;
        }
    };
}

export default usePreparingNotification;
